import {
  DEFAULT_IGLOO_STYLE,
  DEFAULT_ROOM,
  DESALOJADO,
  IGLOO_NAME,
  Msg,
  ROOMS,
  buildClubRoom,
  buildIglooRoom,
  gestureForCommand,
  getClub,
  getFurniture,
  isGameId,
  placementProblem,
  type AnyRoom,
  type ChatPayload,
  type ChatRejectedPayload,
  type EquipRejectedPayload,
  type ChatSaidPayload,
  type ClubId,
  type ClubDirectoryPayload,
  type FurnitureDef,
  type RoomChangedPayload,
  type EquipPayload,
  type GameId,
  type Gesture,
  type GesturePayload,
  type IglooFloorId,
  type IglooPlacement,
  type IglooStyle,
  type IglooWallId,
  type ItemSlot,
  type OpenPanelPayload,
  type Player,
  type Rect,
  type RoomId,
  type RoomState,
} from "@clubap/shared";
import { getStateCallbacks, type Room } from "colyseus.js";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  forgetToken,
  login,
  logout,
  register,
  restoreAccount,
  resumeSession,
  storedToken,
  verifyEmail,
  type Account,
  type Credentials,
  type ProfileUpdate,
  type PublicProfile,
} from "./api.js";
import { AVATAR_PORTRAIT } from "./art/manifest.js";
import { RoomStage, type PlayerSelection } from "./game/RoomStage.js";
import type { EditorOverlay } from "./game/editor.js";
import {
  buyClubFurniture,
  depositToClub,
  fetchClubRoom,
  moveClubFurniture,
  placeClubFurniture,
  removeClubFurniture,
  setClubStyle,
  type ClubRoomSnapshot,
} from "./economy/clubRoom.js";
import {
  buyFurniture,
  fetchIgloo,
  moveFurniture,
  placeFurniture,
  removeFurniture,
  setIglooStyle,
  type IglooSnapshot,
} from "./economy/igloo.js";
import { loadClubCatalog } from "./social/api.js";
import { isAuthFailure, isClosedRoom, joinClubRoom, joinIgloo, joinRoom } from "./net.js";
import { onRouteChange, readRoute, syncRoute } from "./routes.js";
import { relationOf } from "./social/merge.js";
import { useSocial } from "./social/useSocial.js";
import { AccountPanel } from "./ui/AccountPanel.js";
import { ArtShapePanel } from "./ui/ArtShapePanel.js";
import { ItemArtPanel } from "./ui/ItemArtPanel.js";
import { AdminPanel } from "./ui/AdminPanel.js";
import { AuthScreen } from "./ui/AuthScreen.js";
import { Carnet } from "./ui/Carnet.js";
import { ChatPanel } from "./ui/ChatPanel.js";
import { Dock } from "./ui/Dock.js";
import { IconAmigos, IconCartelera, IconJugar, IconMochila } from "./ui/icons.js";
import { IconCaja } from "./ui/iconsIgloo.js";
import { RoomSign } from "./ui/RoomSign.js";
import { Loading } from "./ui/Loading.js";
import { PlayerCard, type CardRelation } from "./ui/PlayerCard.js";
import { PlayerList } from "./ui/PlayerList.js";
import { ProfilePanel } from "./ui/ProfilePanel.js";
import { WelcomeProfile } from "./ui/WelcomeProfile.js";
import { SocialPanel } from "./ui/SocialPanel.js";
import { AjaGame } from "./ui/AjaGame.js";
import { DemoGame } from "./ui/DemoGame.js";
import { FrozenGame } from "./ui/FrozenGame.js";
import { VolleyGame } from "./ui/VolleyGame.js";
import { ShopPanel } from "./ui/ShopPanel.js";
import { ClubDirectory } from "./ui/ClubDirectory.js";
import { ClubPage } from "./ui/ClubPage.js";
import { ClubProposalsPanel } from "./ui/ClubProposalsPanel.js";
import { ClubTreasuryPanel } from "./ui/ClubTreasuryPanel.js";
import { FoundClubPanel } from "./ui/FoundClubPanel.js";
import { IglooBar } from "./ui/IglooBar.js";
import { IglooPanel } from "./ui/IglooPanel.js";
import { IglooStylePanel } from "./ui/IglooStylePanel.js";
import { EditorPanel } from "./ui/EditorPanel.js";

/**
 * El id de la solicitud pendiente con alguien, buscándola por cuenta.
 *
 * Aceptar y rechazar piden el id de la SOLICITUD, pero la tarjeta sólo conoce a la
 * persona: lo que tiene delante es un avatar, no una fila de una bandeja. La
 * traducción vive acá, que es donde está el estado social, y no en la tarjeta —
 * pasarle la lista entera de solicitudes para que busque sería darle un dato que no
 * es suyo.
 */
function pendingRequestId(
  requests: readonly { id: string; from: { id: string }; to: { id: string } }[],
  accountId: string,
): string | null {
  const found = requests.find((entry) => entry.from.id === accountId || entry.to.id === accountId);
  return found?.id ?? null;
}

const REJECTION_TEXT: Record<ChatRejectedPayload["reason"], string> = {
  rate_limited: "Vas muy rápido, espera un momento.",
  empty: "Escribe algo primero.",
  too_long: "Ese mensaje es demasiado largo.",
  filtered: "Ese mensaje no se puede enviar.",
};

/*
 * Por qué el servidor no te dejó ponerte algo.
 *
 * El servidor mandaba `EquipRejected` desde el principio y el cliente no lo escuchaba:
 * una negativa era un clic que no hacía nada. Los tres primeros no salen de la mochila
 * —sólo enseña lo tuyo, en su propia ranura— así que si aparecen, el estado del cliente
 * y el de la base no coinciden y hay que decirlo, no tragárselo.
 */
const EQUIP_REJECTION_TEXT: Record<EquipRejectedPayload["reason"], string> = {
  rate_limited: "Vas muy rápido, espera un momento.",
  not_owned: "No tienes ese ítem.",
  unknown_item: "Ese ítem ya no existe.",
  unknown_slot: "Esa ranura no existe.",
  wrong_slot: "Ese ítem no va en esa ranura.",
};

/** Se mantiene acotado; el registro es del servidor, no del DOM. */
const MAX_LOG = 60;

/**
 * Reintentos de reconexión antes de rendirse. El servidor tarda unos segundos en
 * volver cuando `tsx watch` lo reinicia — cada guardado del editor de mapas lo
 * hace — así que 8 intentos a 1.5s cubren ese hueco con margen.
 */
const MAX_RECONNECT_ATTEMPTS = 8;

/**
 * El enlace que venía en la URL, leído UNA vez al cargar el módulo.
 *
 * Fuera de React y no en un efecto, y las dos cosas importan. Fuera de un efecto porque
 * el arranque tiene que saber si hay un token de restablecimiento ANTES de decidir si
 * reanuda la sesión guardada: sabiéndolo después habría una carrera entre reanudar y
 * cambiar la contraseña de esa misma cuenta. Y una sola vez porque leer y limpiar es una
 * operación que se consume: bajo StrictMode los efectos corren dos veces, y el segundo
 * canje de un token de un solo uso siempre falla.
 *
 * Limpiar la URL en el acto no es cosmético. Un token en la barra de direcciones acaba
 * en el historial y en la lista de "páginas recientes", y estos enlaces son
 * credenciales: en una máquina compartida —la sala de cómputo, sin ir más lejos— el
 * siguiente en sentarse hereda uno vivo.
 */
function takeEmailLink(): { reset: string | null; verify: string | null } {
  if (typeof window === "undefined") return { reset: null, verify: null };

  const params = new URLSearchParams(window.location.search);
  const reset = params.get("reset");
  const verify = params.get("verify");

  if (reset || verify) {
    window.history.replaceState(null, "", window.location.pathname);
  }

  return { reset, verify };
}

const EMAIL_LINK = takeEmailLink();

/**
 * La ruta con la que se abrió el juego, leída UNA vez al cargar el módulo.
 *
 * Igual que `EMAIL_LINK`, y por lo mismo: es la intención con la que alguien llegó, no
 * un estado que cambie. Leerla dentro de un efecto la volvería a aplicar cada vez que el
 * efecto se reejecute, y entraríamos al taller otra vez cada vez que se reconecta.
 */
const RUTA_INICIAL = readRoute();

interface Session {
  roomId: RoomId;
  /** Puerta por la que se entró, para resolver el punto de llegada. */
  viaElement: string | null;
  /**
   * Esta sesión es TU IGLÚ, y entonces `roomId` es a dónde lleva su puerta de salida.
   *
   * Un campo opcional y no una unión a propósito: las transiciones que ya existían
   * —entrar, cruzar una puerta, reconectar tras una caída, renombrarse— siguen armando
   * la sesión de siempre y significan lo mismo. SALIR del iglú, en particular, no
   * necesitó una línea: su puerta manda `Msg.RoomChanged` como cualquier otra y el
   * manejador de siempre arma una sesión sin esta marca.
   */
  igloo?: true;
  /**
   * Esta sesión es la SALA DE UN CLUB, y `roomId` es a dónde lleva su salida.
   *
   * Tercer sitio y misma forma que `igloo`: un campo opcional en vez de una unión, para
   * que las transiciones que ya existían sigan significando lo mismo. Salir tampoco
   * necesitó nada — su puerta manda `Msg.RoomChanged` como cualquier otra.
   */
  club?: ClubId;
}

/**
 * La marca del iglú, para arrastrarla al rehacer una sesión.
 *
 * Existe para que reconectar sea un `...` y no un ternario repetido tres veces: las
 * reconexiones —caída, reintento, cambio de nombre— tienen que devolverte donde estabas,
 * y olvidarse en una de ellas te sacaría del cuarto sin decir nada.
 */
function iglooMark(session: Session): { igloo?: true; club?: ClubId } {
  if (session.club) return { club: session.club };
  return session.igloo ? { igloo: true } : {};
}

/**
 * ¿Se soltó sobre la caja de guardar?
 *
 * La caja es DOM y flota sobre el canvas, así que la pregunta no se le puede hacer al
 * mundo de Pixi: se le hace al documento, con el punto donde acabó el dedo. Vive fuera
 * del componente porque no mira ningún estado — sólo el sitio donde se soltó.
 */
/**
 * El cuarto decorable con el que la UI está hablando ahora.
 *
 * Es la forma de "un sitio que se decora", independiente de si es el iglú de alguien o la
 * sala de un club: los mismos muebles, el mismo arrastre y los mismos paneles, con otro
 * endpoint detrás y otro saldo delante.
 */
interface Decor {
  /** Con `balance` ya resuelto: el saldo propio o la caja del club, según el sitio. */
  snapshot: IglooSnapshot;
  /** Si a QUIEN MIRA le dejan tocar. En un club, sólo al presidente. */
  canDecorate: boolean;
  buy: (furnitureId: string) => Promise<void>;
  place: (furnitureId: string, x: number, y: number) => Promise<IglooPlacement[]>;
  move: (placementId: string, x: number, y: number) => Promise<IglooPlacement[]>;
  remove: (placementId: string) => Promise<IglooPlacement[]>;
  style: (floor: IglooFloorId, wall: IglooWallId) => Promise<IglooStyle>;
  build: (placements: IglooPlacement[], style: IglooStyle) => AnyRoom;
  onPlacements: (placements: IglooPlacement[]) => void;
  onStyle: (style: IglooStyle) => void;
}

/** Lo que el stage cuenta al soltar un mueble: dónde cayó, en mundo y en pantalla. */
interface PieceDrop {
  placementId: string;
  x: number;
  y: number;
  clientX: number;
  clientY: number;
}

function sobreLaCaja(clientX: number, clientY: number): boolean {
  const target = document.elementFromPoint(clientX, clientY);
  return target?.closest("[data-iglu-soltar='guardar']") != null;
}

export function App() {
  const [account, setAccount] = useState<Account | null>(null);
  const [booting, setBooting] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const [session, setSession] = useState<Session | null>(null);
  /**
   * Por qué NO estás en ninguna sala, cuando la cuenta sigue puesta.
   *
   * Va aparte de `authError` porque se pinta en otro sitio: aquél es de la pantalla de
   * ingreso —que exige no tener cuenta— y éste del hueco entre tener cuenta y estar
   * dentro. Meterlos en el mismo estado fue lo que dejó el "Conectando…" eterno.
   */
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [inRoom, setInRoom] = useState(false);
  const [messages, setMessages] = useState<ChatSaidPayload[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [players, setPlayers] = useState<PlayerSelection[]>([]);
  const [selected, setSelected] = useState<PlayerSelection | null>(null);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  /* Qué minijuego está abierto, o ninguno. Antes era un booleano: con el puesto del
     parque hay más de uno, y cuál se abre lo decide el objeto que tocaste. */
  const [game, setGame] = useState<GameId | null>(null);
  /*
   * El partido de volley, abierto o no.
   *
   * Lo abre la cancha del coliseo (`kind: "cancha"`) y nada más: fue un botón del dock
   * mientras la cancha no existía, y se quitó en cuanto la tuvo. La entrada correcta es
   * llegar hasta ella, porque quien comprueba la cercanía es el servidor.
   */
  const [volleyOpen, setVolleyOpen] = useState(false);
  /** Lo que el servidor dice que llevo puesto, para que la tienda lo refleje. */
  const [myEquipped, setMyEquipped] = useState<string[]>([]);
  const [accountOpen, setAccountOpen] = useState(false);
  /**
   * Lo que hay en tu iglú, mientras estés dentro: catálogo, comprado y colocado.
   *
   * Vive en React y no en el catálogo compartido porque es lo único de una sala que no
   * puede venir en el bundle: los muebles son de una cuenta y salen de la base. Se pide
   * una vez al entrar y se actualiza con la respuesta de cada compra o colocación.
   */
  const [igloo, setIgloo] = useState<IglooSnapshot | null>(null);
  /** Lo que hay en la sala del club donde estás, si estás en una. */
  const [clubRoom, setClubRoom] = useState<ClubRoomSnapshot | null>(null);
  /** El modo decorar: la barra a la vista y los muebles arrastrables. */
  const [decorando, setDecorando] = useState(false);
  /** La rejilla del piso, que además dice dónde acaba el suelo. */
  const [rejilla, setRejilla] = useState(true);
  /** La tienda de muebles, que es un panel aparte de la barra. */
  const [iglooShop, setIglooShop] = useState(false);
  /** El panel de piso y pared, detrás de la chapa del iglú. */
  const [iglooStyleOpen, setIglooStyleOpen] = useState(false);
  /** La caja común del club, que es lo único que un miembro cualquiera puede tocar. */
  const [cajaOpen, setCajaOpen] = useState(false);
  /**
   * El directorio de sedes, con el filtro del tablón que se tocó.
   *
   * `null` es cerrado; `{ facultyId: null }` es abierto SIN filtro. Son dos cosas
   * distintas y por eso no se guarda sólo la facultad: un `null` a secas no sabría
   * distinguir "cerrado" de "todos los clubes".
   */
  const [directorio, setDirectorio] = useState<{ facultyId: string | null } | null>(null);
  /** La ficha de un club, abierta desde el directorio o desde la pestaña de clubes. */
  const [clubPage, setClubPage] = useState<string | null>(null);
  /** El formulario de fundación. */
  const [fundando, setFundando] = useState(false);
  /** La bandeja de solicitudes, sólo para admins. */
  const [proposalsOpen, setProposalsOpen] = useState(false);
  /**
   * La pieza que va en la mano ahora mismo, arrastrada desde la barra.
   *
   * Vive en React y no en el stage porque lo que se dibuja pegado al cursor es DOM: una
   * ficha idéntica a la de la barra. Lo que el stage pinta es la SOMBRA en el suelo, que
   * es la mitad que sí es del mundo.
   */
  const [drag, setDrag] = useState<{
    furniture: FurnitureDef;
    clientX: number;
    clientY: number;
  } | null>(null);
  /** Biblioteca de dibujos: moldear la colisión sin colocar el objeto. Sólo dev. */
  const [shapesOpen, setShapesOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);

  /** El token de `?reset=`, mientras no se haya usado. Su presencia abre esa pantalla. */
  const [resetToken, setResetToken] = useState<string | null>(EMAIL_LINK.reset);
  /** Resultado de un enlace del correo, para decirlo en la pantalla de ingreso. */
  const [linkNotice, setLinkNotice] = useState<string | null>(null);

  const [profileOpen, setProfileOpen] = useState(false);
  /*
   * El paso de bienvenida, sólo tras registrarse.
   *
   * NO se limpia con la sesión: bajo StrictMode el efecto monta, limpia y vuelve a
   * montar, así que meterlo en esa limpieza lo cerraría antes de verse.
   */
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  /** La cuenta ajena que un admin está editando, o null. */
  const [manage, setManage] = useState<PublicProfile | null>(null);
  /** Ventana para deshacer un borrado hecho por error. */
  const [undo, setUndo] = useState<{ playerId: string; username: string } | null>(null);
  const [socialOpen, setSocialOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  /**
   * El editor de mapas, cuando está activo. Sólo en desarrollo: el endpoint que
   * escribe `rooms.ts` no existe en un build de producción.
   */
  const [editor, setEditor] = useState<EditorOverlay | null>(null);
  /*
   * Escenario limpio: esconde la UI DEL JUEGO y deja el mundo solo (F3).
   *
   * Es para mirar lo que se está construyendo sin que el carnet tape una esquina y el
   * chat otra. No esconde los paneles de HERRAMIENTA —el editor, las siluetas, el
   * configurador de ítems— porque esconder aquello con lo que trabajas convierte el
   * modo en un callejón: te deja mirando un mundo que ya no puedes tocar.
   */
  const [uiHidden, setUiHidden] = useState(false);
  /** Contador para redibujar el panel: el editor muta su borrador en su sitio. */
  const [editorTick, setEditorTick] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const roomRef = useRef<Room<RoomState> | null>(null);
  /* El stage vivo, para poder repintar los muebles del iglú sin rehacer el canvas. */
  const stageRef = useRef<RoomStage | null>(null);
  /* Los dos manejadores del arrastre, siempre en su versión de este render. Ver dónde
     se le pasan al stage. */
  const validateRef = useRef<(rect: Rect, placementId: string) => boolean>(() => true);
  const dropRef = useRef<(drop: PieceDrop) => void>(() => {});
  /* El cuarto que se decora, para que los manejadores no dependan de cuál es. */
  const decorRef = useRef<Decor | null>(null);

  /**
   * Reconexión tras una caída. Sin esto, cualquier reinicio del servidor dejaba al
   * jugador varado en un "Conectando…" sin salida: `onLeave` ponía `session` en
   * null con la cuenta aún puesta, y ninguna rama de la UI ofrecía reintentar.
   */
  const retryRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
   * Confirmar el correo, si se llegó por ese enlace.
   *
   * Va aparte de la reanudación y no depende de ella: confirmar una dirección no necesita
   * sesión —el enlace ES la prueba— así que funciona igual abriéndolo en el navegador del
   * teléfono, donde nadie ha entrado nunca.
   *
   * Sin dependencias porque `EMAIL_LINK` se leyó y consumió al cargar el módulo: bajo
   * StrictMode este efecto corre dos veces, pero el segundo pase no encuentra token.
   */
  useEffect(() => {
    const token = EMAIL_LINK.verify;
    if (!token) return;

    let cancelled = false;

    void (async () => {
      try {
        await verifyEmail(token);
        if (!cancelled) setLinkNotice("Correo confirmado. Ya puedes recuperar tu cuenta con él.");
      } catch (cause) {
        if (!cancelled) {
          setLinkNotice(cause instanceof Error ? cause.message : "Ese enlace ya no sirve.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Reanudar la sesión guardada antes de mostrar nada: si el token sirve, el
  // jugador entra directo sin volver a escribir su contraseña.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      /*
       * Con un enlace de restablecimiento no se reanuda nada.
       *
       * Quien lo abre viene a cambiar la contraseña de esa cuenta, y el servidor cierra
       * TODAS las sesiones al hacerlo — incluida la que se acabara de reanudar acá.
       * Entrar al mundo para que un segundo después lo echen es peor que no entrar; y
       * peor todavía si el enlace lo abre alguien en una máquina donde quedó la sesión
       * de otro, porque entraría con la cuenta ajena antes de tocar nada.
       */
      const resumed = EMAIL_LINK.reset ? null : await resumeSession();
      if (cancelled) return;

      if (resumed) {
        setAccount(resumed);
        /* El catálogo de clubes ANTES de entrar al mundo, no después.
           `getClub()` lee un registro en memoria que este proceso tiene que llenar, y un
           avatar con insignia dibujado antes de que llegue sale sin ella y no se repinta:
           el catálogo no es estado de React, así que nada la vuelve a pedir. */
        await loadClubCatalog().catch((cause) => {
          console.error("[clubap] no se pudo cargar el catálogo de clubes", cause);
        });
        if (cancelled) return;
        setSession(sesionDeRuta());
        if (RUTA_INICIAL.kind === "club") setClubPage(RUTA_INICIAL.clubId);
      }
      setBooting(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Hacia AFUERA: la barra de direcciones dice dónde estás.
   *
   * Se corrige detrás del movimiento, nunca se apila: entrar al taller o abrir una ficha
   * no son pasos que el jugador dio en el historial, así que el botón de atrás sigue
   * sirviendo para volver al enlace con el que llegó. Ver `routes.ts`.
   */
  useEffect(() => {
    if (!account) return;

    if (clubPage) {
      syncRoute({ kind: "club", clubId: clubPage });
      return;
    }

    /*
     * En el taller SÓLO si de verdad estás parado en él.
     *
     * La sesión de un cuarto decorable guarda en `roomId` la sala de VUELTA, no dónde
     * estás: entrar al taller y abrir desde ahí tu contenedor dejaba la barra diciendo
     * `/taller` con el contenedor en pantalla. Esas dos marcas son lo que distingue
     * "estoy en esta sala" de "vuelvo a esta sala".
     */
    const enElTaller =
      session?.roomId === "taller" && !session.igloo && session.club === undefined;

    syncRoute(enElTaller ? { kind: "taller" } : { kind: "juego" });
  }, [account, clubPage, session?.roomId, session?.igloo, session?.club]);

  /*
   * Y hacia ADENTRO: atrás, adelante, o un enlace pegado estando ya dentro.
   *
   * Sólo se escucha `popstate`, que es navegación del usuario y nunca lo que escribe el
   * efecto de arriba. Por eso los dos sentidos no se pueden morder.
   */
  useEffect(() => {
    return onRouteChange((route) => {
      if (route.kind === "club") {
        setClubPage(route.clubId);
        return;
      }

      setClubPage(null);
      setSession((current) => {
        if (!current) return current;
        const destino: RoomId = route.kind === "taller" ? "taller" : DEFAULT_ROOM;
        if (current.roomId === destino && !current.igloo && !current.club) return current;
        return { roomId: destino, viaElement: null };
      });
    });
  }, []);

  /** Vuelve a la pantalla de ingreso, descartando el token que ya no sirve. */
  const dropSession = useCallback((message: string | null) => {
    forgetToken();
    setAccount(null);
    setSession(null);
    setInRoom(false);
    setSocialOpen(false);
    setVolleyOpen(false);
    setAuthError(message);
  }, []);

  /**
   * El efecto de sesión depende de esto y NO del objeto `account`.
   *
   * Es la diferencia entre que funcione y que no: actualizar el saldo crea un
   * `account` nuevo, y con el objeto como dependencia el efecto se reejecutaba en
   * cada moneda ganada o gastada. Su limpieza cierra los paneles y **deja la
   * sala**, así que abrir la tienda —que lee el saldo al montarse— se cerraba sola
   * y reconectaba al jugador.
   *
   * El efecto sólo usa la cuenta como guarda de "hay sesión"; nunca lee sus
   * campos. Un id estable es exactamente lo que necesita.
   */
  /*
   * Tu propia entrada del roster, para poder abrir tu ficha desde el dock.
   *
   * Sale del estado de la sala y no de `account` porque la ficha se dibuja con lo que
   * la sala dice —color, clubes, lo que llevas puesto— y mezclar las dos fuentes
   * dejaría tu ficha enseñando algo distinto de lo que ven los demás.
   */
  const self = players.find((player) => player.sessionId === roomRef.current?.sessionId);

  const accountId = account?.id ?? null;

  /*
   * F3 y no F2: F2 ya abre el editor de mapas, y dos cosas en la misma tecla es lo
   * que hace que nadie se acuerde de ninguna.
   *
   * Depende de `isDev` y no de `account`, por lo de siempre en este archivo: el objeto
   * cambia de referencia con cada saldo, y un efecto que dependa de él se reengancha
   * en bucle.
   */
  const isDev = account?.dev === true;

  useEffect(() => {
    if (!isDev) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "F3") return;
      // Escribiendo en un campo no, aunque F3 no se teclee: el reflejo es el mismo
      // que protege al resto de atajos, y salir de aquí es barato.
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      event.preventDefault();
      setUiHidden((hidden) => !hidden);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDev]);

  /* Al dejar de ser dev —o al cerrar sesión— la UI vuelve sola: si no, una cuenta
     normal podría heredar un escenario limpio del que no sabe salir. */
  useEffect(() => {
    if (!isDev) setUiHidden(false);
  }, [isDev]);

  /*
   * El token de la sesión, para los paneles que abren una sala PROPIA.
   *
   * Se lee una vez por render en vez de dentro del JSX: es una cadena estable, y pasarla
   * como prop es lo que permite que el efecto de conexión del volley dependa sólo de ella.
   */
  const sessionToken = storedToken();

  /*
   * La marca de cuenta de desarrollo, en una ref.
   *
   * El efecto de sesión sólo puede depender de `accountId`, así que no puede leer
   * `account.dev` directamente sin volver a reconectar en cada cambio de saldo. Una
   * ref le da el valor fresco sin entrar en las dependencias.
   */
  const devRef = useRef(false);
  devRef.current = account?.dev === true;

  /*
   * Entrar o salir de un club pasa por HTTP, pero la insignia vive en el estado de la
   * sala: se le pide que relea. Es una intención — el servidor decide qué lee.
   */
  const handleProfileChanged = useCallback(() => {
    roomRef.current?.send(Msg.RefreshProfile, {});
  }, []);

  /*
   * El canal social, con ciclo de vida PROPIO.
   *
   * Depende de `accountId` y de nada más: cruzar una puerta, ganar una moneda o
   * reconectar al campus no lo tocan. Si dependiera de `session` volvería a caerse en
   * cada viaje, y una conversación abierta se cerraría al cambiar de sala.
   */
  const { state: socialState, actions: socialActions } = useSocial(accountId, {
    onProfileChanged: handleProfileChanged,
    onSessionExpired: () => dropSession("Tu sesión venció. Vuelve a entrar."),
  });

  // Este efecto es dueño de la sesión completa: conexión, canvas y limpieza. Eso
  // es lo que lo hace correcto bajo StrictMode, y lo que hace que cambiar de sala
  // sea sólo actualizar `session`.
  useEffect(() => {
    if (!session || !accountId) return;

    const token = storedToken();
    if (!token) {
      dropSession("Tu sesión venció. Vuelve a entrar.");
      return;
    }

    let cancelled = false;
    let room: Room<RoomState> | null = null;
    let stage: RoomStage | null = null;
    /** Suelta los suscriptores de estado; se llena al montar la sala. */
    let cleanupPlayers: (() => void) | null = null;

    void (async () => {
      try {
        /*
         * Al iglú se entra por NOMBRE y con el dueño, no por el id de una sala.
         *
         * Es la única entrada del juego que no pasa por una puerta, y no puede pasar:
         * una puerta sólo sabe llevar a un id del catálogo, y los iglús son uno por
         * cuenta. Salir sí es una puerta normal, así que el viaje de vuelta no tiene
         * nada especial.
         */
        const joined = session.club
          ? await joinClubRoom(session.club, session.roomId, { token })
          : session.igloo
          ? await joinIgloo(accountId, session.roomId, { token })
          : await joinRoom(session.roomId, {
              token,
              ...(session.viaElement ? { viaElement: session.viaElement } : {}),
            });

        if (cancelled) {
          await joined.leave();
          return;
        }

        room = joined;
        roomRef.current = joined;

        joined.onMessage<ChatSaidPayload>(Msg.ChatSaid, (payload) => {
          setMessages((previous) => [...previous, payload].slice(-MAX_LOG));
          stage?.showBubble(payload.sessionId, payload.text);
        });

        joined.onMessage<ChatRejectedPayload>(Msg.ChatRejected, (payload) => {
          setNotice(REJECTION_TEXT[payload.reason]);
        });

        joined.onMessage<EquipRejectedPayload>(Msg.EquipRejected, (payload) => {
          setNotice(EQUIP_REJECTION_TEXT[payload.reason]);
        });

        // El servidor aprobó la puerta: dejamos esta sala y entramos a la otra.
        joined.onMessage<RoomChangedPayload>(Msg.RoomChanged, (payload) => {
          if (cancelled) return;
          setInRoom(false);
          setSession({ roomId: payload.to, viaElement: payload.viaElement });
        });

        /*
         * Llegaste a un objeto que abre un panel: el mostrador del Sweet, el puesto
         * del parque, la cancha del coliseo.
         *
         * Lo abre el SERVIDOR y no el clic, igual que una puerta: es él quien sabe
         * que estás al lado. Y el juego que viene se comprueba contra el catálogo
         * antes de usarlo — un mensaje es dato de red, no una promesa del compilador.
         */
        /*
         * Tocaste el tablón de clubes. Se abre el directorio, no se entra a nada.
         *
         * Antes acá llegaba "entra a este club": el mapa tenía un contenedor por club y
         * tocarlo metía. Con los clubes fundables eso dejó de poder existir —un elemento
         * del mapa es dato fijo y no puede enumerar algo que nace mientras el juego
         * corre—, así que el tablón abre una lista y entrar es el segundo gesto.
         *
         * El filtro viene del SITIO, no de quién mira: el del edificio de la FIEC enseña
         * los suyos, el del patio los enseña todos.
         */
        joined.onMessage<ClubDirectoryPayload>(Msg.ClubDirectory, (payload) => {
          if (cancelled) return;
          setDirectorio({ facultyId: payload.facultyId });
        });

        joined.onMessage<OpenPanelPayload>(Msg.OpenPanel, (payload) => {
          if (cancelled) return;

          if (payload.panel === "shop") {
            setShopOpen(true);
            return;
          }

          if (payload.panel === "volley") {
            setVolleyOpen(true);
            return;
          }

          if (payload.panel === "game" && isGameId(payload.ref)) setGame(payload.ref);
        });

        const $ = getStateCallbacks(joined);

        /*
         * Firma de lo que esta lista MUESTRA: identidad, insignias y aspecto; nunca
         * posición.
         *
         * Es la red que impide que un callback mal elegido devuelva el bug de los
         * renders por movimiento — si la firma no cambió, no se toca el estado de React
         * aunque el avatar lleve mil pasos dados. Lo puesto entra en la firma porque la
         * tarjeta lo dibuja: fuera de ella, cambiarse el sombrero no repintaría la
         * figura de quien tienes abierto.
         */
        let signature = "";

        const syncPlayers = () => {
          const list: PlayerSelection[] = [];
          joined.state.players.forEach((player, sessionId) => {
            list.push({
              sessionId,
              nickname: player.nickname,
              color: player.color,
              accountId: player.accountId,
              clubs: [...player.clubs],
              equipped: [...player.equipped],
            });
          });

          const next = list
            .map(
              (p) =>
                `${p.sessionId}:${p.accountId}:${p.nickname}:${p.color}:${p.clubs.join("+")}:${p.equipped.join("+")}`,
            )
            .join("|");

          if (next === signature) return;
          signature = next;

          setPlayers(list);

          /*
           * La tarjeta abierta también es una COPIA, y se quedaba vieja.
           *
           * Se refresca por `sessionId` desde la lista recién construida, así que entrar
           * a un club se le ve al instante a quien tienes delante. Si ese jugador ya no
           * está en la sala, se cierra: un perfil de alguien que se fue no tiene qué
           * mostrar.
           */
          setSelected((current) =>
            current
              ? (list.find((entry) => entry.sessionId === current.sessionId) ?? null)
              : current,
          );
        };

        /*
         * Suscribirse al MAPA no alcanza, y ésta es la trampa que documenta CLAUDE.md:
         * `onChange` de una colección NO es recursivo. Con sólo `onAdd`/`onRemove`, la
         * lista se copiaba al entrar cada jugador y se quedaba así — entrar a un club
         * cambia `player.clubs` sin tocar el mapa, y la insignia no aparecía hasta cruzar
         * una puerta.
         */
        const detach: Array<() => void> = [];

        /**
         * SÓLO las dos listas que cambian estando dentro. **Nunca `$(player).onChange`**:
         * ése se dispara con `x`/`y`/`walking`/`facing`, o sea a 20 Hz por avatar, y
         * colgarle un `setState` de React reconstruiría la lista entera en cada paso de
         * cada jugador.
         *
         * Lo demás —cuenta, nombre, color— lo fija el servidor al entrar y no vuelve a
         * cambiar, así que el `onAdd` del mapa ya trae su valor.
         *
         * `equipped` faltaba, y era la misma trampa de `clubs` una segunda vez: la firma
         * ya contaba lo puesto —el carnet lo dibuja— pero nadie avisaba de que hubiera
         * cambiado. Ponerse algo llegaba al mundo y a los demás, y la figura del carnet
         * abierto se quedaba con la copia vieja hasta cruzar una puerta.
         */
        const watchPlayer = (player: Player) => {
          detach.push($(player).clubs.onAdd(syncPlayers));
          detach.push($(player).clubs.onRemove(syncPlayers));
          /*
           * Las dos, porque equipar es `clear()` y luego `push()` en el servidor:
           * quitarse lo último sólo dispara `onRemove`, y sin él la gorra seguiría
           * dibujada después de quitártela.
           */
          detach.push($(player).equipped.onAdd(syncPlayers));
          detach.push($(player).equipped.onRemove(syncPlayers));
        };

        detach.push(
          $(joined.state).players.onAdd((player) => {
            watchPlayer(player);
            syncPlayers();
          }),
        );
        detach.push($(joined.state).players.onRemove(syncPlayers));

        // Los suscriptores se sueltan con la sesión: la sala siguiente trae otros.
        cleanupPlayers = () => {
          for (const off of detach) off();
          detach.length = 0;
        };

        joined.onLeave((code) => {
          // 1000 es una salida limpia, incluida la que provocamos al cambiar de
          // sala; todo lo demás es una caída, y de una caída se vuelve solo.
          if (cancelled || code === 1000) return;

          /*
           * Salvo que nos hayan ECHADO por abrir el juego en otro sitio.
           *
           * De eso no se vuelve reintentando: la otra pestaña sigue ahí, y reconectar
           * sería quitarle el sitio a ella para que ella nos lo quite a nosotros. Se
           * corta y se explica, que es la única salida que no es un bucle.
           */
          if (code === DESALOJADO) {
            roomRef.current = null;
            setInRoom(false);
            setSession(null);
            setSessionError("Abriste el juego en otra pestaña. Este sigue tu avatar allá.");
            return;
          }

          roomRef.current = null;
          setInRoom(false);
          retryTimerRef.current = setTimeout(() => {
            // Se vuelve a donde estabas, iglú incluido: caerse del cuarto propio no
            // debería sacarte al campus.
            setSession({ roomId: session.roomId, viaElement: null, ...iglooMark(session) });
          }, 1500);
        });

        const container = containerRef.current;
        if (!container) throw new Error("stage container missing");

        /*
         * La geometría de una sala del campus ya viaja en el bundle: el catálogo es
         * compartido y el cliente lo tiene entero. La de un iglú no puede —sus muebles
         * son de una cuenta y viven en la base— así que se pide una vez por HTTP y se
         * arma con la MISMA función con la que el servidor lo está simulando. Dos
         * lecturas del mismo dato con la misma función: lo que se ve es contra lo que se
         * choca.
         */
        let def: AnyRoom = ROOMS[session.roomId];

        if (session.club) {
          const snapshot = await fetchClubRoom(session.club);
          if (cancelled) {
            await joined.leave();
            return;
          }

          setClubRoom(snapshot);
          def = buildClubRoom(session.club, session.roomId, snapshot.placements, snapshot.style);
        } else if (session.igloo) {
          const snapshot = await fetchIgloo();
          if (cancelled) {
            await joined.leave();
            return;
          }

          setIgloo(snapshot);
          // El estilo entra en el armado INICIAL, no sólo al cambiarlo: sin esto se
          // entraba siempre al cuarto con el piso y la pared por defecto, y el color
          // elegido no aparecía hasta tocar el panel.
          def = buildIglooRoom(accountId, session.roomId, snapshot.placements, snapshot.style);
        }

        const created = await RoomStage.create(container, joined, def, {
          /*
           * Se lee de una ref y no de `account`, por lo mismo que el efecto depende
           * de `accountId`: meter el objeto entero acá lo devolvería a la lista de
           * dependencias y volveríamos a reconectar en cada cambio de saldo.
           */
          dev: devRef.current,
          onNotice: setNotice,
          onSelectPlayer: setSelected,
          onSelfEquipped: setMyEquipped,
          /*
           * El arrastre de un mueble YA puesto lo resuelve el stage, que es quien tiene
           * el gesto; las reglas y el viaje al servidor son de acá.
           *
           * Las dos van por una ref y no directas: este efecto se monta una vez por sala
           * y los manejadores se rehacen en cada render —dependen de lo que hay puesto—,
           * así que pasarlos tal cual dejaría al stage llamando para siempre a la versión
           * del primer render, con la lista de muebles vacía.
           */
          onDragValidate: (rect, placementId) => validateRef.current(rect, placementId),
          onPieceDropped: (drop) => dropRef.current(drop),
          onEditorChange: (overlay) => {
            /*
             * `cancelled` importa acá.
             *
             * `RoomStage.create` es asíncrono y el overlay restaura su estado dentro
             * de esa espera. Si la sesión cambió entre medias —justo lo que pasa al
             * reiniciarse el servidor— la limpieza ya corrió, y sin esta guarda este
             * callback volvería a montar el panel apuntando a un stage destruido.
             */
            if (cancelled) return;
            setEditor(overlay.active ? overlay : null);
            setEditorTick((tick) => tick + 1);
          },
        });

        if (cancelled) {
          created.destroy();
          return;
        }

        stage = created;
        stageRef.current = created;
        setInRoom(true);
        setSessionError(null);
        retryRef.current = 0;
      } catch (cause) {
        if (cancelled) return;

        if (isAuthFailure(cause)) {
          dropSession("Tu sesión venció. Vuelve a entrar.");
          return;
        }

        /*
         * La sala dejó de estar abierta: se vuelve al campus en vez de reintentar ocho
         * veces contra una puerta que el servidor ya no va a abrir.
         *
         * Un cuarto decorable también cae acá cuando el servidor lo rechaza
         * (`igloo_private`, `club_private`), y ahí se vuelve a la sala de la que se
         * venía y no al inicio: ese cuarto TIENE de dónde venías, y mandarte al otro
         * extremo del campus por tocar un contenedor que no es tuyo castiga el error
         * mucho más de lo que cuesta. Sin la marca, así que se entra como sala normal y
         * no se vuelve a pedir lo que acaban de negar.
         */
        if (isClosedRoom(cause)) {
          if (session.club || session.igloo) {
            setSession({ roomId: session.roomId, viaElement: null });
            return;
          }

          if (session.roomId !== DEFAULT_ROOM) {
            setSession({ roomId: DEFAULT_ROOM, viaElement: null });
            return;
          }
        }

        // El servidor puede estar reiniciándose (un guardado del editor, un
        // deploy): se reintenta la misma sala antes de rendirse.
        if (retryRef.current < MAX_RECONNECT_ATTEMPTS) {
          retryRef.current += 1;
          retryTimerRef.current = setTimeout(() => {
            setSession({
              roomId: session.roomId,
              viaElement: session.viaElement,
              ...iglooMark(session),
            });
          }, 1500);
          return;
        }

        /*
         * Se acabaron los intentos.
         *
         * El mensaje va a `sessionError` y no a `authError`, y ahí estaba un fallo feo:
         * `authError` sólo se pinta en la pantalla de INGRESO, que exige no tener cuenta
         * puesta. Con la cuenta puesta —que es el caso— nadie lo leía nunca, y la app se
         * quedaba en "Conectando…" para siempre, sin decir qué pasó ni ofrecer salida.
         */
        console.error("[clubap] join failed", cause);
        retryRef.current = 0;
        setSession(null);
        setSessionError("No se pudo conectar. ¿Está corriendo el servidor?");
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      cleanupPlayers?.();
      cleanupPlayers = null;
      stage?.destroy();
      stageRef.current = null;
      void room?.leave();
      roomRef.current = null;
      // La tarjeta y la lista apuntan a jugadores de la sala que dejamos.
      setSelected(null);
      setPlayers([]);
      setRosterOpen(false);
      setShopOpen(false);
      setGame(null);
      /* Los muebles son de la sala que dejas — literalmente: fuera del iglú no hay nada
         que decorar, y el panel abierto contra una sala vieja sería el mismo fallo que
         el registro del chat que sobrevivía al viaje. */
      setIglooShop(false);
      setIglooStyleOpen(false);
      setCajaOpen(false);
      /* El directorio es del TABLÓN que tocaste, así que se va con la sala. La ficha y
         el formulario no: son de tu cuenta y sobreviven al viaje, igual que el panel
         social. */
      setDirectorio(null);
      setClubRoom(null);
      setDecorando(false);
      setDrag(null);
      setIgloo(null);
      setAccountOpen(false);
      setShapesOpen(false);
      setProfileOpen(false);
      setManage(null);
      setEditor(null);
      /*
       * El panel social NO se cierra acá, a diferencia del resto.
       *
       * Los demás muestran cosas de la sala que dejas; éste es de tu cuenta y su
       * canal ni se entera del viaje. Cerrarlo al cruzar una puerta te cortaría una
       * conversación a la mitad por caminar.
       */
    };
  }, [session, accountId, dropSession]);

  /*
   * El registro del chat se queda en la sala que dejas.
   *
   * El servidor ya lo tenía bien —`broadcast` llega a la instancia de sala y a nadie
   * más— pero el panel es del cliente y su historial sobrevivía al viaje: cruzabas una
   * puerta y seguías leyendo lo que se dijo al otro lado, con gente que ya no está
   * ahí. Se ve exactamente igual que un chat global, que es lo que no es.
   *
   * Depende del SITIO y no de `session`, y esa diferencia es el punto: una reconexión
   * al mismo sitio —un reinicio del servidor, un guardado del editor— no cambia este
   * valor y el efecto no vuelve a correr, así que la conversación sobrevive a la caída.
   * Sólo la borra cambiar de sitio.
   *
   * El iglú y la sala de un club cuentan como sitios aparte aunque su sesión guarde el
   * id de la sala a la que se vuelve: con el id a secas, entrar al cuarto propio y salir
   * dejaría el registro intacto y adentro se leerían frases dichas en otra sala. El club
   * lleva CUÁL en la clave — son cinco salas distintas, no una.
   */
  const placeKey = session
    ? session.club
      ? `club:${session.club}`
      : session.igloo
        ? "igloo"
        : session.roomId
    : null;

  useEffect(() => {
    setMessages([]);
  }, [placeKey]);

  // El aviso de moderación se descarta solo.
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  /*
   * El deshacer dura poco a propósito: cubre el clic equivocado. Pasado eso, la
   * cuenta sigue restaurable —el borrado es suave— pero ya no desde acá.
   */
  useEffect(() => {
    if (!undo) return;
    const timer = setTimeout(() => setUndo(null), 15000);
    return () => clearTimeout(timer);
  }, [undo]);

  /**
   * La sesión con la que se estrena, según la ruta.
   *
   * Una ruta que pide el taller entra al taller; si la cuenta no es `dev`, el servidor
   * responde 403 y la rama de sala cerrada devuelve al campus — que es exactamente el
   * comportamiento que ya existía y por eso no hay nada especial que escribir acá.
   */
  const sesionDeRuta = useCallback((): Session => {
    if (RUTA_INICIAL.kind === "taller") return { roomId: "taller", viaElement: null };
    return { roomId: DEFAULT_ROOM, viaElement: null };
  }, []);

  const handleAuth = useCallback(async (mode: "login" | "register", credentials: Credentials) => {
    setAuthBusy(true);
    setAuthError(null);

    try {
      const authenticated =
        mode === "login" ? await login(credentials) : await register(credentials);
      setAccount(authenticated);
      setMessages([]);
      setWelcomeOpen(mode === "register");
      /* Igual que al reanudar: el catálogo de clubes tiene que estar antes de pintar el
         primer avatar, o su insignia sale en blanco y nada la vuelve a pedir. */
      await loadClubCatalog().catch((cause) => {
        console.error("[clubap] no se pudo cargar el catálogo de clubes", cause);
      });
      setSession(sesionDeRuta());
      if (RUTA_INICIAL.kind === "club") setClubPage(RUTA_INICIAL.clubId);
    } catch (cause) {
      setAuthError(cause instanceof Error ? cause.message : "Algo falló.");
    } finally {
      setAuthBusy(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setAccount(null);
    setSession(null);
    setInRoom(false);
    setMessages([]);
    setSocialOpen(false);
    setAuthError(null);
  }, []);

  const handleGesture = useCallback((gesture: Gesture) => {
    roomRef.current?.send(Msg.Gesture, { gesture } satisfies GesturePayload);
  }, []);

  const handleSend = useCallback(
    (text: string) => {
      /*
       * `/bailar` es un gesto, no una línea de chat.
       *
       * Se traduce acá y no en el servidor porque el chat pasa por el filtro y el rate
       * limit de mensajes, y un gesto no es un mensaje: mandarlo como texto gastaría una
       * de las cuatro líneas cada tres segundos que tiene el jugador para hablar.
       */
      const gesture = gestureForCommand(text);
      if (gesture) {
        handleGesture(gesture);
        return;
      }

      roomRef.current?.send(Msg.Chat, { text } satisfies ChatPayload);
    },
    [handleGesture],
  );

  /**
   * Ir a tu iglú: la misma maniobra que cruzar una puerta, pedida desde el carnet.
   *
   * Se guarda la sala actual como sala de vuelta, así que salir te deja donde estabas.
   * Estando dentro no hace nada: el cuarto propio es uno solo, y volver a pedirlo
   * reconectaría por nada.
   */
  const handleOpenIgloo = useCallback(() => {
    setSession((current) =>
      current && !current.igloo
        ? { roomId: current.roomId, viaElement: null, igloo: true }
        : current,
    );
  }, []);

  /**
   * El cuarto que se decora ahora mismo, sea de quien sea.
   *
   * Existe para que la barra, la tienda y el panel de piso no sepan de quién es el sitio.
   * Son la misma UI sobre los mismos muebles; lo único que cambia es a qué endpoint va
   * cada acción, con qué saldo se paga y si a ti te dejan tocarla. Sin esto habría dos
   * copias de la barra que empezarían iguales y acabarían distintas.
   *
   * `canDecorate` lo dice el SERVIDOR en la respuesta, no se deduce acá. La UI lo usa
   * para no ofrecer lo que va a rebotar; quien decide sigue siendo él.
   */
  const decor: Decor | null =
    session?.club && clubRoom
      ? {
          /* La caja del club ocupa el sitio del saldo: es con lo que se paga acá. */
          snapshot: { ...clubRoom, balance: clubRoom.treasury },
          canDecorate: clubRoom.canDecorate,
          buy: (furnitureId: string) =>
            buyClubFurniture(session.club!, furnitureId).then((result) => {
              setClubRoom((current) =>
                current ? { ...current, treasury: result.treasury } : current,
              );
            }),
          place: (furnitureId: string, x: number, y: number) =>
            placeClubFurniture(session.club!, furnitureId, x, y),
          move: (placementId: string, x: number, y: number) =>
            moveClubFurniture(session.club!, placementId, x, y),
          remove: (placementId: string) => removeClubFurniture(session.club!, placementId),
          style: (floor: IglooFloorId, wall: IglooWallId) =>
            setClubStyle(session.club!, floor, wall),
          build: (placements: IglooPlacement[], style: IglooStyle) =>
            buildClubRoom(session.club!, session.roomId, placements, style),
          onPlacements: (placements: IglooPlacement[]) =>
            setClubRoom((current) => (current ? { ...current, placements } : current)),
          onStyle: (style: IglooStyle) =>
            setClubRoom((current) => (current ? { ...current, style } : current)),
        }
      : session?.igloo && igloo && accountId
        ? {
            snapshot: igloo,
            canDecorate: true,
            buy: (furnitureId: string) =>
              buyFurniture(furnitureId as never).then((result) => {
                handleBalance(result.balance);
                setIgloo((current) =>
                  current ? { ...current, owned: result.owned, balance: result.balance } : current,
                );
              }),
            place: (furnitureId: string, x: number, y: number) =>
              placeFurniture(furnitureId as never, x, y),
            move: (placementId: string, x: number, y: number) =>
              moveFurniture(placementId, x, y),
            remove: (placementId: string) => removeFurniture(placementId),
            style: (floor: IglooFloorId, wall: IglooWallId) => setIglooStyle(floor, wall),
            build: (placements: IglooPlacement[], style: IglooStyle) =>
              buildIglooRoom(accountId, session.roomId, placements, style),
            onPlacements: (placements: IglooPlacement[]) =>
              setIgloo((current) => (current ? { ...current, placements } : current)),
            onStyle: (style: IglooStyle) =>
              setIgloo((current) => (current ? { ...current, style } : current)),
          }
        : null;

  /* El adaptador, siempre en su versión de este render: los manejadores lo leen de acá
     en vez de capturarlo, que es lo que los deja sin dependencias y sin quedarse viejos. */
  decorRef.current = decor;

  /**
   * Repinta el cuarto con lo que haya ahora.
   *
   * Al servidor no hay que decirle nada: el endpoint que acaba de escribir ya avisó a la
   * sala por su cuenta, que es lo que hace que también se entere cuando el cambio viene
   * de otra pestaña — o, en un club, de otro miembro mirando.
   */
  const repintar = useCallback((placements: IglooPlacement[], style: IglooStyle) => {
    const actual = decorRef.current;
    if (!actual) return;
    stageRef.current?.setRoom(actual.build(placements, style));
  }, []);

  const handleIglooChanged = useCallback(
    (placements: IglooPlacement[]) => {
      const actual = decorRef.current;
      if (!actual) return;
      actual.onPlacements(placements);
      repintar(placements, actual.snapshot.style);
    },
    [repintar],
  );

  /** Cambió el piso o la pared: se rehace el fondo, los muebles se quedan donde están. */
  const handleStyleChanged = useCallback(
    (style: IglooStyle) => {
      const actual = decorRef.current;
      if (!actual) return;
      actual.onStyle(style);
      repintar(actual.snapshot.placements, style);
    },
    [repintar],
  );

  /**
   * La caja del mueble si se soltara con el cursor en este punto.
   *
   * Centrada en el cursor, que es lo que uno espera al arrastrar algo: la pieza va
   * "en la mano", no colgando de su esquina.
   */
  const rectUnderCursor = useCallback((furniture: FurnitureDef, clientX: number, clientY: number) => {
    const world = stageRef.current?.worldAt(clientX, clientY);
    if (!world) return null;

    return {
      x: Math.round(world.x - furniture.size.width / 2),
      y: Math.round(world.y - furniture.size.height / 2),
      width: furniture.size.width,
      height: furniture.size.height,
    };
  }, []);

  /**
   * Arrastrar una pieza NUEVA desde la barra hasta el piso.
   *
   * El gesto se sigue con listeners en `window` y no en la ficha: el cursor se va del
   * botón en el primer píxel, y un `pointermove` sobre un elemento que ya no está
   * debajo no llega. `setPointerCapture` haría lo mismo, pero se pierde en cuanto el
   * árbol se repinta — y acá la barra se repinta con cada compra.
   */
  const handlePickUp = useCallback(
    (furniture: FurnitureDef, event: ReactPointerEvent) => {
      event.preventDefault();
      setDrag({ furniture, clientX: event.clientX, clientY: event.clientY });
    },
    [],
  );

  useEffect(() => {
    if (!drag) return;

    const move = (event: PointerEvent) => {
      setDrag((current) =>
        current ? { ...current, clientX: event.clientX, clientY: event.clientY } : current,
      );

      const rect = rectUnderCursor(drag.furniture, event.clientX, event.clientY);
      // Sobre el basurero no se pinta sombra: ahí no se coloca, se guarda.
      if (!rect || sobreLaCaja(event.clientX, event.clientY)) {
        stageRef.current?.showDrop(null, false);
        return;
      }

      stageRef.current?.showDrop(
        rect,
        placementProblem(drag.furniture.id, rect.x, rect.y) === null,
      );
    };

    const up = (event: PointerEvent) => {
      setDrag(null);
      stageRef.current?.showDrop(null, false);

      // Soltar sobre el basurero una pieza que todavía no está puesta no hace nada:
      // ya estaba guardada. Devolverla en silencio es la respuesta correcta.
      if (sobreLaCaja(event.clientX, event.clientY)) return;

      const rect = rectUnderCursor(drag.furniture, event.clientX, event.clientY);
      if (!rect) return;

      const problem = placementProblem(drag.furniture.id, rect.x, rect.y);
      if (problem) {
        setNotice(problem);
        return;
      }

      void (decorRef.current?.place(drag.furniture.id, rect.x, rect.y) ?? Promise.resolve([]))
        .then(handleIglooChanged)
        .catch((cause: unknown) => {
          setNotice(cause instanceof Error ? cause.message : "No se pudo colocar.");
        });
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, handleIglooChanged, rectUnderCursor]);

  /**
   * Se soltó un mueble que YA estaba puesto: se mueve, o se guarda si cayó en la caja.
   *
   * El stage manda las coordenadas de pantalla además de las de mundo justo para esto:
   * el destino de guardar es un elemento del DOM que flota encima del canvas.
   */
  const handlePieceDropped = useCallback(
    (drop: PieceDrop) => {
      if (sobreLaCaja(drop.clientX, drop.clientY)) {
        void (decorRef.current?.remove(drop.placementId) ?? Promise.resolve([]))
          .then(handleIglooChanged)
          .catch((cause: unknown) => {
            setNotice(cause instanceof Error ? cause.message : "No se pudo guardar.");
          });
        return;
      }

      const placement = decorRef.current?.snapshot.placements.find(
        (entry) => entry.id === drop.placementId,
      );
      const furniture = placement ? getFurniture(placement.furnitureId) : undefined;
      if (!furniture) return;

      const problem = placementProblem(furniture.id, drop.x, drop.y);
      if (problem) {
        // La pieza ya volvió sola a su sitio: el stage repinta desde el dato, no desde
        // el arrastre. Acá sólo queda decir por qué no se quedó donde la soltaste.
        setNotice(problem);
        return;
      }

      void (decorRef.current?.move(drop.placementId, drop.x, drop.y) ?? Promise.resolve([]))
        .then(handleIglooChanged)
        .catch((cause: unknown) => {
          setNotice(cause instanceof Error ? cause.message : "No se pudo mover.");
        });
    },
    [handleIglooChanged],
  );

  /**
   * ¿Vale la caja donde está ahora el mueble levantado? Colorea su sombra.
   *
   * Se resuelve con `placementProblem`, la MISMA función con la que el servidor va a
   * decidir: así el verde y el rojo del arrastre no pueden decir una cosa y el endpoint
   * otra.
   */
  const validateDrag = useCallback(
    (rect: { x: number; y: number }, placementId: string): boolean => {
      const placement = decorRef.current?.snapshot.placements.find(
        (entry) => entry.id === placementId,
      );
      if (!placement) return true;
      return placementProblem(placement.furnitureId, rect.x, rect.y) === null;
    },
    [],
  );

  /* Las refs que el stage llama, al día en cada render. Se asignan durante el render a
     propósito: un efecto las dejaría un render por detrás, y el arrastre pasa entre
     medias. */
  validateRef.current = validateDrag;
  dropRef.current = handlePieceDropped;

  /*
   * El modo decorar vive en React, y el stage tiene que enterarse.
   *
   * Va en un efecto y no en las opciones de creación porque se enciende y se apaga
   * estando dentro: el stage se monta una vez por sala y esto cambia muchas veces.
   * `inRoom` está en las dependencias para volver a aplicarlo cuando el stage se rehace
   * —una reconexión, un guardado del editor—, que si no dejaría la barra encendida
   * sobre un mundo que ya no sabe nada de arrastres.
   */
  useEffect(() => {
    stageRef.current?.setDecorating(decorando);
    stageRef.current?.setGrid(decorando && rejilla);
  }, [decorando, rejilla, inRoom]);

  const handleEquip = useCallback((slot: ItemSlot, itemId: string | null) => {
    roomRef.current?.send(Msg.Equip, { slot, itemId } satisfies EquipPayload);
  }, []);

  const handleBalance = useCallback((balance: number) => {
    setAccount((current) => (current ? { ...current, coins: balance } : current));
  }, []);

  /**
   * La contraseña ya cambió: se sale a la pantalla de ingreso.
   *
   * Se descarta el token guardado porque el servidor acaba de cerrar todas las sesiones
   * de esa cuenta; conservarlo dejaría a la app reintentando con una credencial muerta.
   * Y no se entra solo: acabar un restablecimiento escribiendo la contraseña nueva una
   * vez más es lo que confirma que la recuerda.
   */
  const handleResetDone = useCallback(() => {
    forgetToken();
    setResetToken(null);
    setAccount(null);
    setSession(null);
    setInRoom(false);
    setAuthError(null);
    setLinkNotice("Contraseña cambiada. Entra con la nueva.");
  }, []);

  /*
   * Renombrarse obliga a reconectar: la sala lee el nombre UNA vez, en `onAuth`, así
   * que sin volver a entrar los demás te seguirían viendo con el anterior.
   */
  const handleProfileSaved = useCallback((update: ProfileUpdate) => {
    if (update.self) setAccount(update.player);

    /*
     * Nombre y color se leen UNA vez, al entrar a la sala, así que cambiarlos exige
     * volver a entrar. Es una sola reconexión aunque cambien los dos: `setSession` con
     * la misma sala rehace el viaje, y hacerlo dos veces sería el bucle de join/leave
     * que ya nos costó una tarde.
     */
    if (update.renamed || update.recolored) {
      if (update.self) {
        setSession((current) =>
          current ? { roomId: current.roomId, viaElement: null, ...iglooMark(current) } : current,
        );
      } else {
        setNotice("Guardado. Lo verá cuando vuelva a entrar.");
      }
    }

    setManage(null);
    setSelected(null);
  }, []);

  /** Borrarse a uno mismo revoca la sesión en el servidor: acá sólo queda salir. */
  const handleSelfDeleted = useCallback(() => {
    forgetToken();
    setAccount(null);
    setSession(null);
    setInRoom(false);
    setProfileOpen(false);
    setSocialOpen(false);
    setMessages([]);
    setWelcomeOpen(false);
    setAuthError("Cuenta eliminada. Tu nombre queda reservado 30 días.");
  }, []);

  const handleUndo = useCallback(async () => {
    if (!undo) return;

    try {
      await restoreAccount(undo.playerId);
      setNotice(`${undo.username} vuelve a estar activa.`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "No se pudo deshacer.");
    } finally {
      setUndo(null);
    }
  }, [undo]);

  /**
   * Cómo estoy con el jugador cuya tarjeta está abierta.
   *
   * Se resuelve contra el estado social, que ya lo sabe, en vez de preguntarle al
   * servidor cada vez que se toca un avatar.
   */
  const relationWith = useCallback(
    (target: PlayerSelection | null): CardRelation =>
      !target || !target.accountId || !accountId
        ? "none"
        : relationOf(socialState, accountId, target.accountId),
    [accountId, socialState],
  );

  /* Estable, para que el efecto de Escape del panel no se resuscriba en cada render. */
  const closeSocial = useCallback(() => setSocialOpen(false), []);

  /** Solicitudes por responder más mensajes sin leer: lo que pide atención. */
  const socialPending =
    socialState.incoming.length +
    socialState.friends.reduce((total, friend) => total + friend.unread, 0);

  return (
    <div className={`app${uiHidden ? " app--limpio" : ""}`}>
      {/* Siempre montado para que el ref exista antes de crear el stage. */}
      <div className="stage" ref={containerRef} />

      {/* La única pista de que la UI no se rompió. Sin esto, esconderla sin querer se
          vive como un cuelgue y la salida es recargar. */}
      {uiHidden && <span className="limpio-aviso herramienta">F3 · UI oculta</span>}

      {booting && <Loading label="Cargando…" />}

      {!booting && !account && (
        <AuthScreen
          busy={authBusy}
          /* El aviso del enlace y el error de ingreso comparten hueco: nunca coinciden,
             porque venir de un enlace es lo primero que pasa y fallar al entrar es
             siempre después. */
          error={authError ?? linkNotice}
          resetToken={resetToken}
          onSubmit={handleAuth}
          onResetDone={handleResetDone}
        />
      )}

      {/*
        Con la cuenta puesta pero sin sala hay DOS estados, y confundirlos costaba un
        cuelgue mudo: se está entrando, o se dejó de entrar. El segundo necesita decir
        qué pasó y ofrecer una salida — antes caía en el mismo "Conectando…" eterno.
      */}
      {!booting && account && !inRoom && !sessionError && (
        <Loading
          label={
            session
              ? `Entrando a ${session.igloo ? IGLOO_NAME : ROOMS[session.roomId].name}…`
              : "Conectando…"
          }
        />
      )}

      {!booting && account && !inRoom && sessionError && (
        <div className="overlay">
          <div className="panel loading">
            <p>{sessionError}</p>
            <button
              className="btn"
              type="button"
              onClick={() => {
                setSessionError(null);
                setSession({ roomId: DEFAULT_ROOM, viaElement: null });
              }}
            >
              Volver al campus
            </button>
          </div>
        </div>
      )}

      {account && inRoom && session && (
        <>
          {/*
            El HUD tiene cuatro sitios y cada uno responde a una pregunta: el carnet
            arriba-izquierda dice quién eres, el letrero arriba-centro dónde estás, la
            cartelera arriba-derecha quién más hay, y el dock abajo-derecha qué puedes
            hacer. El borde inferior queda repartido entre el chat y el dock, cada uno
            en su esquina.

            Antes eran siete controles en dos grupos arriba, con "Cerrar sesión" a un
            clic de "Jugar". Repartirlos por tema es lo que deja meter fichas grandes:
            el dock son objetivos táctiles de 86px que en el teléfono sí se aciertan.
          */}
          {/*
            Decorando, la barra OCUPA la fila de arriba: el carnet y el letrero se
            esconden en vez de pelearse con ella por el sitio. Es lo que hace el editor
            de Club Penguin, y además quita una redundancia — el saldo lo enseña la
            barra mientras dura.
          */}
          {!decorando && (
          <Carnet
            account={account}
            worn={myEquipped}
            onOpenAccount={() => setAccountOpen(true)}
            onOpenProfile={() => setProfileOpen(true)}
            /* Dentro del iglú no se ofrece: ya estás en él, y pedirlo otra vez sólo
               reconectaría. Se sale por su puerta, como de cualquier sala. */
            onOpenIgloo={session.igloo ? null : handleOpenIgloo}
            /*
              Las MISMAS dos condiciones que el editor, y ninguna sobra:
              `import.meta.env.DEV` porque el endpoint que reescribe `artShapes.ts` es
              un plugin de Vite que no existe en producción, y `account.dev` porque
              moldear una colisión la cambia para todo el campus, no sólo para quien la
              moldea.
            */
            onOpenShapes={import.meta.env.DEV && account.dev ? () => setShapesOpen(true) : null}
            /* Las MISMAS dos condiciones, y por lo mismo: el endpoint que reescribe
               `itemArt.ts` es un plugin de Vite que no existe en producción, y colocar un
               ítem lo cambia para todo el campus. */
            onOpenItems={import.meta.env.DEV && account.dev ? () => setItemsOpen(true) : null}
            /* Esconder el botón es comodidad, no seguridad: cada endpoint de
               `/api/admin` vuelve a comprobar la marca. */
            onOpenAdmin={account.admin ? () => setAdminOpen(true) : null}
            /* Aprobar clubes es del admin y de nadie más (regla 14). Esconderlo es
               comodidad: el endpoint vuelve a comprobarlo. */
            onOpenClubProposals={account.admin ? () => setProposalsOpen(true) : null}
            onLogout={() => void handleLogout()}
          />
          )}

          {!decorando && (
            <RoomSign
              name={
                session.club
                  ? `Sala · ${getClub(session.club)?.name ?? "club"}`
                  : session.igloo
                    ? IGLOO_NAME
                    : ROOMS[session.roomId].name
              }
            />
          )}

          <Dock
            items={[
              {
                id: "jugar",
                label: "JUGAR",
                icon: <IconJugar />,
                active: game !== null,
                onClick: () => setGame("demo"),
              },
              {
                id: "mochila",
                label: "MOCHILA",
                icon: <IconMochila />,
                /*
                 * Abre TU ficha, que es donde vive ahora la mochila.
                 *
                 * Eran dos ventanas para lo mismo. La ficha ya enseñaba tu aspecto y la
                 * mochila lo repetía para poder cambiarlo, así que cambiarse era cerrar
                 * una y abrir la otra mirando la misma figura dos veces.
                 */
                active: selected?.sessionId === roomRef.current?.sessionId,
                onClick: () =>
                  setSelected((open) =>
                    open?.sessionId === roomRef.current?.sessionId ? null : (self ?? null),
                  ),
              },
              {
                id: "amigos",
                label: "AMIGOS",
                icon: <IconAmigos />,
                /* La cifra sólo aparece cuando hay algo que atender: un cero
                   permanente enseña a ignorar el sitio donde se avisa. */
                badge: socialPending > 0 ? String(socialPending) : undefined,
                active: socialOpen,
                onClick: () => setSocialOpen((open) => !open),
              },
              /*
               * Decorar sólo existe DENTRO de un cuarto decorable, que es donde se decora.
               *
               * Es la excepción que el dock sí admite: no es una acción de cualquier
               * sala disfrazada de ficha, es la acción de ESTA sala. Fuera no aparece,
               * así que el dock del campus sigue teniendo lo que tenía.
               *
               * En la sala de un club la ficha depende de `canDecorate`, que lo dice el
               * servidor: a un miembro que no preside no se le ofrece un botón cuyo
               * primer clic va a rebotar. Esconderlo es cortesía, no seguridad — cada
               * endpoint vuelve a preguntar quién llama.
               */
              ...(decor?.canDecorate
                ? [
                    {
                      id: "decorar",
                      label: "DECORAR",
                      icon: <IconMochila />,
                      active: decorando,
                      onClick: () => setDecorando((open) => !open),
                    },
                  ]
                : []),
              /*
               * La caja, en cambio, es de CUALQUIER miembro: es la mitad del trato.
               * El presidente amuebla, pero paga el club, así que aportar tiene que
               * estar donde está todo el mundo y no detrás del modo decorar.
               */
              ...(session.club
                ? [
                    {
                      id: "caja",
                      label: "CAJA",
                      icon: <IconCaja />,
                      active: cajaOpen,
                      onClick: () => setCajaOpen((open) => !open),
                    },
                  ]
                : []),
            ]}
          />

          {/*
            Quién está en la sala: un disparador discreto arriba a la derecha, y la
            cartelera colgando de él.

            Salió del dock porque no es una de las cosas que se HACEN jugando —es una
            consulta, y de las que se hacen de reojo—, y una ficha de 86px pidiendo la
            misma atención que "jugar" desequilibraba el sitio donde sí se juega. Acá
            arriba, apagado, se lee cuando lo buscas y desaparece cuando no.

            La cuenta se enseña siempre, aunque esté cerrado: saber que hay alguien más
            es la mitad de la razón para abrirlo, y es el dato que justifica que esto
            ocupe una esquina.
          */}
          <button
            className={`roster${rosterOpen ? " roster--on" : ""}`}
            type="button"
            onClick={() => setRosterOpen((open) => !open)}
            aria-expanded={rosterOpen}
            title="Quién está en la sala"
          >
            <IconCartelera size={14} />
            {String(players.length).padStart(2, "0")}
          </button>

          {rosterOpen && (
            <PlayerList
              players={players}
              selfSessionId={roomRef.current?.sessionId ?? null}
              onSelect={setSelected}
              onClose={() => setRosterOpen(false)}
            />
          )}

          <ChatPanel
            messages={messages}
            notice={notice}
            selfSessionId={roomRef.current?.sessionId ?? null}
            onSend={handleSend}
            onGesture={handleGesture}
          />

          {shopOpen && (
            <ShopPanel
              balance={account.coins}
              color={account.color}
              onBalanceChange={handleBalance}
              onClose={() => setShopOpen(false)}
            />
          )}

          {/*
            La barra de decorar: sólo dentro del iglú y sólo con el modo encendido.

            Va FUERA del `overlay` de los paneles a propósito. Un panel modal tapa el
            mundo, y acá el mundo es donde hay que soltar la pieza: la barra tiene que
            flotar sobre él, no sustituirlo.
          */}
          {decorando && decor && (
            <IglooBar
              snapshot={decor.snapshot}
              /* Lo que se puede gastar acá: tus monedas en el iglú, la caja en un club. */
              balance={decor.snapshot.balance}
              caja={session.club !== undefined}
              dragging={drag !== null}
              grid={rejilla}
              onToggleGrid={() => setRejilla((on) => !on)}
              onOpenShop={() => setIglooShop(true)}
              onOpenStyle={() => setIglooStyleOpen(true)}
              onDone={() => setDecorando(false)}
              onPickUp={handlePickUp}
            />
          )}

          {iglooStyleOpen && decor && (
            <IglooStylePanel
              snapshot={decor.snapshot}
              onApply={decor.style}
              onApplied={handleStyleChanged}
              onClose={() => setIglooStyleOpen(false)}
            />
          )}

          {/* La pieza en la mano. Sigue al cursor y no recibe eventos: lo que hay debajo
              tiene que poder contestar quién es. */}
          {drag && (
            <div className="iglu-fantasma" style={{ left: drag.clientX, top: drag.clientY }}>
              <span
                className="iglu-fantasma__figura"
                style={{ background: drag.furniture.kind === "silla" ? "#c0714f" : drag.furniture.kind === "mesa" ? "#a9723f" : "#4c9c5b" }}
              />
            </div>
          )}

          {iglooShop && decor && (
            <IglooPanel
              snapshot={decor.snapshot}
              onBuy={decor.buy}
              caja={session.club !== undefined}
              onClose={() => setIglooShop(false)}
            />
          )}

          {/*
            La caja del club. Se abre desde el dock y la ve cualquier miembro, decore o
            no: aportar es lo que el sistema le pide a quien no preside.
          */}
          {/*
            El directorio de sedes: se abre tocando un tablón del mapa.

            Fuera del `overlay` de los paneles modales no — éste SÍ tapa el mundo, porque
            elegir club es una decisión, no un gesto sobre el suelo.
          */}
          {directorio && (
            <ClubDirectory
              facultyId={directorio.facultyId}
              mine={self?.clubs ?? []}
              onOpenClub={(clubId) => {
                setDirectorio(null);
                setClubPage(clubId);
              }}
              onFound={() => {
                setDirectorio(null);
                setFundando(true);
              }}
              onClose={() => setDirectorio(null)}
            />
          )}

          {clubPage && (
            <ClubPage
              clubId={clubPage}
              onClose={() => setClubPage(null)}
              onEnterRoom={(clubId) => {
                setClubPage(null);
                /* Misma maniobra que cruzar una puerta: la sala actual queda como sala de
                   vuelta, así que salir de la sede te deja donde estabas. */
                setSession((current) =>
                  current ? { roomId: current.roomId, viaElement: null, club: clubId } : current,
                );
              }}
            />
          )}

          {fundando && (
            <FoundClubPanel
              onFounded={() => setFundando(false)}
              onClose={() => setFundando(false)}
            />
          )}

          {proposalsOpen && <ClubProposalsPanel onClose={() => setProposalsOpen(false)} />}

          {cajaOpen && session.club && clubRoom && (
            <ClubTreasuryPanel
              clubId={session.club}
              treasury={clubRoom.treasury}
              balance={account.coins}
              onDeposited={(treasury, balance) => {
                handleBalance(balance);
                setClubRoom((current) => (current ? { ...current, treasury } : current));
              }}
              onClose={() => setCajaOpen(false)}
            />
          )}

          {/*
            Un panel por juego, elegido por id.

            El registro está aquí y no en un archivo de datos a propósito: un juego
            nuevo necesita una pantalla, así que esta línea es exactamente el trabajo
            que falta. Un mapa incompleto no compilaría, que es lo que se quiere.
          */}
          {game === "demo" && (
            <DemoGame
              onFinished={(result) => handleBalance(result.balance)}
              onClose={() => setGame(null)}
            />
          )}

          {game === "aja" && (
            <AjaGame
              onFinished={(result) => handleBalance(result.balance)}
              onClose={() => setGame(null)}
            />
          )}

          {/* El Frozen dibuja TU avatar, así que necesita las dos cosas que lo definen:
              el color de la cuenta y lo que llevas puesto. Las captura al abrirse — ver
              la ref `look` en el panel— para que un cambio de saldo no tire el canvas. */}
          {game === "frozen" && (
            <FrozenGame
              color={account.color}
              equipped={myEquipped}
              onFinished={(result) => handleBalance(result.balance)}
              onClose={() => setGame(null)}
            />
          )}

          {/*
            El volley abre su PROPIA sala, así que no pasa por `game`: no es una sesión
            de puntaje como las otras dos, es un partido con otro jugador dentro. El token
            se le pasa como prop porque su efecto de conexión depende sólo de él —
            depender del objeto `account` reconectaría la cancha con cada moneda ganada.
          */}
          {volleyOpen && sessionToken && (
            <VolleyGame
              token={sessionToken}
              onBalance={handleBalance}
              onClose={() => setVolleyOpen(false)}
            />
          )}

          {/*
            El editor exige DOS condiciones, y ninguna sobra.

            `import.meta.env.DEV` porque el endpoint que reescribe `rooms.ts` es un
            plugin de Vite que no existe en un build de producción: el panel ahí sería
            un formulario que siempre da 404. Y `account.dev` porque en un servidor de
            desarrollo compartido cualquiera con una cuenta podría reescribir el mapa
            de todos.
          */}
          {import.meta.env.DEV && account.dev && editor && (
            /* `tick` como PROP y no como `key`: una key nueva REMONTA el panel,
               y eso borraba el mensaje de guardado al instante y robaba el foco
               del campo de etiqueta en cada tecla. Un prop que cambia sólo
               re-renderiza, que es lo que se quiere. */
            <EditorPanel
              editor={editor}
              tick={editorTick}
              onChanged={() => setEditorTick((tick) => tick + 1)}
            />
          )}

          {import.meta.env.DEV && account.dev && itemsOpen && (
            <ItemArtPanel onClose={() => setItemsOpen(false)} />
          )}

          {import.meta.env.DEV && account.dev && shapesOpen && (
            <ArtShapePanel onClose={() => setShapesOpen(false)} />
          )}

          {accountOpen && (
            <AccountPanel
              account={account}
              onAccountChange={setAccount}
              onClose={() => setAccountOpen(false)}
            />
          )}

          {selected && (
            <PlayerCard
              player={selected}
              portrait={AVATAR_PORTRAIT}
              isSelf={selected.sessionId === roomRef.current?.sessionId}
              canManage={account.admin && selected.sessionId !== roomRef.current?.sessionId}
              onManage={setManage}
              relation={relationWith(selected)}
              onAddFriend={(targetId) => {
                void socialActions.request(targetId);
              }}
              onEditProfile={() => {
                setSelected(null);
                setProfileOpen(true);
              }}
              coins={account.coins}
              onEquip={handleEquip}
              onOpenShop={() => {
                setSelected(null);
                setShopOpen(true);
              }}
              onAccept={() => {
                const id = pendingRequestId(socialState.incoming, selected.accountId);
                if (id) void socialActions.accept(id);
                setSelected(null);
              }}
              onReject={() => {
                const id = pendingRequestId(socialState.incoming, selected.accountId);
                if (id) void socialActions.reject(id);
                setSelected(null);
              }}
              onCancelRequest={() => {
                const id = pendingRequestId(socialState.outgoing, selected.accountId);
                if (id) void socialActions.cancel(id);
                setSelected(null);
              }}
              onMessage={() => {
                setSelected(null);
                setSocialOpen(true);
                void socialActions.openThread(selected.accountId);
              }}
              onClose={() => setSelected(null)}
            />
          )}

          {welcomeOpen && (
            <WelcomeProfile
              onSaved={(update) => {
                handleProfileSaved(update);
                setWelcomeOpen(false);
              }}
              onSkip={() => setWelcomeOpen(false)}
            />
          )}

          {profileOpen && (
            <ProfilePanel
              initial={{
                username: account.username,
                color: account.color,
                bio: account.bio,
                faculty: account.faculty,
                program: account.program,
                semester: account.semester,
              }}
              onSaved={handleProfileSaved}
              onDeleted={handleSelfDeleted}
              onClose={() => setProfileOpen(false)}
            />
          )}

          {adminOpen && (
            <AdminPanel
              me={account.id}
              /* Reusa el mismo panel de perfil que la ficha: administrar una cuenta
                 desde el listado y desde su tarjeta tiene que ser lo mismo, o son
                 dos formularios que se van separando. */
              onManage={(profile) => {
                setAdminOpen(false);
                setManage(profile);
              }}
              onClose={() => setAdminOpen(false)}
            />
          )}

          {manage && (
            <ProfilePanel
              initial={{
                username: manage.username,
                color: manage.color,
                bio: manage.bio,
                faculty: manage.faculty,
                program: manage.program,
                semester: manage.semester,
              }}
              playerId={manage.id}
              onSaved={handleProfileSaved}
              onDeleted={() => {
                setUndo({ playerId: manage.id, username: manage.username });
                setManage(null);
                setSelected(null);
              }}
              onClose={() => setManage(null)}
            />
          )}

          {undo && (
            <div className="panel undo" role="status">
              <span className="undo__text">Eliminaste a {undo.username}.</span>
              <button className="btn btn--small" type="button" onClick={() => void handleUndo()}>
                Deshacer
              </button>
            </div>
          )}
        </>
      )}

      {/*
        El panel social vive FUERA del bloque de la sala, a diferencia de todos los
        demás.

        Los otros muestran cosas del sitio donde estás y no tienen sentido sin él. Éste
        es de tu cuenta: montarlo dentro lo desmontaría en cada viaje —perdiendo el
        borrador a medio escribir— y lo apagaría justo mientras el mundo reconecta, que
        es cuando más sirve poder seguir leyendo a alguien.
      */}
      {socialOpen && accountId && (
        <SocialPanel
          me={accountId}
          state={socialState}
          actions={socialActions}
          canAppoint={account?.admin === true}
          onClose={closeSocial}
        />
      )}
    </div>
  );
}
